import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCookieConsent } from 'web/components/CookieConsentContext';
import { shouldUseEmulators } from 'web/components/FirebaseContext/common';
import useErrorReporter from 'web/hooks/useErrorReporter';
import settings from 'web/utils/settings';

type IdentifyType = {
  email: string;
  id: string;
};

type HubspotContextType = {
  onPathChanged: (path: string) => void;
  setUser: ({ email, id }: IdentifyType) => void;
  reset: () => void;
  openChat: () => void;
  chatReady: boolean;
} | null;

const HubspotContext = React.createContext<HubspotContextType>(null);

type Hsq = Array<
  | ['trackPageView']
  | ['setPath', string]
  | ['identify', IdentifyType]
  | ['revokeCookieConsent']
  | ['doNotTrack']
  | ['doNotTrack', { track: true }]
>;

declare global {
  interface Window {
    _hsq?: Hsq;
    hsConversationsOnReady?: (() => void)[];
    HubSpotConversations?: {
      widget: {
        open: () => void;
        load: () => void;
        refresh: () => void;
        status: () => {
          loaded: boolean;
        };
      };
    };
  }
}

const HubspotProvider = ({ children }: { children: React.ReactNode }) => {
  const errorReporter = useErrorReporter();
  const { measurementAllowed } = useCookieConsent();
  const state = useRef({
    isIdentify: false,
    paths: [],
  });
  const wasEnabled = useRef(false);

  useEffect(
    () => () => {
      if (wasEnabled.current) {
        window._hsq?.push(['doNotTrack']);
        window._hsq?.push(['revokeCookieConsent']);
      }
    },
    [],
  );

  const [chatReady, setChatReady] = useState(false);
  const enableHubspotQueue = useCallback(() => {
    window._hsq = window._hsq || [];
    if (!wasEnabled.current) {
      window._hsq.push(['doNotTrack', { track: true }]);
      wasEnabled.current = true;
    }
    return window._hsq;
  }, []);

  const appendHsqPath = useCallback(
    (path: string) => {
      if (!measurementAllowed) {
        return;
      }
      const hsq = enableHubspotQueue();
      hsq.push(['setPath', path]);
      hsq.push(['trackPageView']);
    },
    [enableHubspotQueue, measurementAllowed],
  );

  const setUser = useCallback(
    ({ email, id }: { email: string; id: string }) => {
      if (!measurementAllowed) {
        return;
      }
      enableHubspotQueue().push([
        'identify',
        {
          email,
          id,
        },
      ]);
      state.current.paths.forEach((p, i) => {
        if (i === 0) return; // exclude first auto call `trackPageView` after script loads
        appendHsqPath(p);
      });
      state.current.isIdentify = true;
      state.current.paths = [];
    },
    [appendHsqPath, enableHubspotQueue, measurementAllowed],
  );

  const reset = useCallback(() => {
    if (measurementAllowed && state.current.isIdentify) {
      window._hsq?.push(['revokeCookieConsent']);
    }
  }, [measurementAllowed]);

  useEffect(() => {
    const hubId = settings.hubspot.hubId;
    if (!hubId || shouldUseEmulators) {
      return;
    }

    if (!measurementAllowed) {
      setChatReady(false);
      if (wasEnabled.current) {
        window._hsq?.push(['doNotTrack']);
        window._hsq?.push(['revokeCookieConsent']);
      }
      state.current.isIdentify = false;
      state.current.paths = [];
      wasEnabled.current = false;
      return;
    }

    enableHubspotQueue();
    window.hsConversationsOnReady = [
      () => {
        setChatReady(true);
      },
    ];
    const existingScript = document.getElementById('hs-script-loader');
    if (existingScript) {
      const chatWidget = window.HubSpotConversations?.widget;
      if (chatWidget) {
        chatWidget.load();
        setChatReady(true);
      }
      appendHsqPath(window.location.pathname);
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hs-script-loader';
    script.src = `https://js.hs-scripts.com/${hubId}.js`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, [appendHsqPath, enableHubspotQueue, measurementAllowed]);

  const value = useMemo(
    () => ({
      onPathChanged: (path: string) => {
        if (!measurementAllowed) {
          return;
        }
        if (state.current.isIdentify) {
          appendHsqPath(path);
        } else if (state.current.paths.length === 0 || state.current.paths[state.current.paths.length - 1] !== path) {
          state.current.paths.push(path);
        }

        const chatWidget = window.HubSpotConversations?.widget;
        if (chatWidget) {
          if (chatWidget.status().loaded) {
            // Refresh forces chat widget to reread the path and reinitialize
            // with the right chatflow, or unload if no chatflows exist for that path
            chatWidget.refresh();
          } else {
            // Once unloaded, chat widgets needs to be reloaded on path change
            // It's safe to call load multiple times
            chatWidget.load();
          }
        }
      },
      setUser,
      openChat: () => {
        if (!measurementAllowed) {
          return;
        }
        const chatWidget = window.HubSpotConversations?.widget;

        if (chatWidget?.status().loaded) {
          chatWidget.open();
        } else {
          // TODO: we might want to propagate this error to the caller, e.g. to show an error in the UI
          errorReporter.report(new Error(`Failed to open Hubspot Chat: widget is not initialized on this page`));
        }
      },
      chatReady,
      reset,
    }),
    [setUser, chatReady, appendHsqPath, errorReporter, measurementAllowed, reset],
  );

  return <HubspotContext.Provider value={value}>{children}</HubspotContext.Provider>;
};

export { HubspotProvider };
export default HubspotContext;
